import axios from 'axios'

export const API_HS_NI_ENDPOINT = process.env.REACT_APP_API_HS_NI_ENDPOINT

export const OUT_OF_HOURS_ACTIONS = {
  send: 'send',
  abort: 'abort'
}

const notificacoesInteligentes = {}

notificacoesInteligentes.getQrCodeHiperApiV1 = async (api, secret, provider = 'hiperapi-v1') => {
  // provider permite distinguir o Baileys estável (hiperapi-v1) do canário
  // (hiperapi-v1-next). Default v1 mantém compatibilidade com chamadas antigas.
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/generateQRCode/hiperapi-v1`, {
    clientApi: api,
    clientSecret: secret,
    provider
  })

  return response?.data
}

notificacoesInteligentes.getQrCodeHiperApiV2 = async (api, secret) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/generateQRCode/hiperapi-v2`, {
    clientApi: api,
    clientSecret: secret
  })

  return response?.data
}

notificacoesInteligentes.whatsappBusinessConnectionHiperApiV3CloudApi = async (api, secret, data = {}) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/whatsappBusinessConnection/hiperapi-v3-cloud-api`, {
    clientApi: api,
    clientSecret: secret,
    ...data
  })

  return response?.data
}

// Embedded Signup da Meta (ES v4): troca o `code` de uso único que o FB.login
// devolve pelo token da conexão. O master é quem fala com a Graph API — aqui só
// repassamos code/wabaId/phoneNumberId (e o pin, quando a Meta exige verificação
// em duas etapas). Erros (400) vêm com { error: true, code, message } e quem
// chama decide o que fazer com cada `code` (ex.: pedir o PIN de novo).
notificacoesInteligentes.embeddedSignup = async (api, secret, { code, wabaId, phoneNumberId, pin } = {}) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/whatsappBusinessConnection/embedded-signup`, {
    clientApi: api,
    clientSecret: secret,
    code,
    wabaId,
    phoneNumberId,
    ...(pin ? { pin } : {})
  })

  return response?.data
}

notificacoesInteligentes.updateProvider = async (api, secret, provider) => {
  const response = await axios.patch(`${API_HS_NI_ENDPOINT}/instances/microservices/provider`, {
    clientApi: api,
    clientSecret: secret,
    provider
  })

  return response?.data
}

notificacoesInteligentes.getInformations = async (api, secret) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/informations`, {
    clientApi: api,
    clientSecret: secret
  })

  return response?.data
}

notificacoesInteligentes.getWabaSnapshot = async (api, secret, { force = false } = {}) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/whatsappBusiness/snapshot`, {
    clientApi: api,
    clientSecret: secret,
    force
  })

  return response?.data
}

// Consumo do período desta conexão: mensagens, mix por categoria, gasto e —
// quando é o cliente quem paga a Meta — o custo dele lá. O backend decide o que
// vem pelo regime da instância; na linha de crédito o custo da Meta nem sai de
// lá, porque é a margem da GLA.
notificacoesInteligentes.getConsumo = async (api, secret, { de, ate } = {}) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/consumo`, {
    clientApi: api,
    clientSecret: secret,
    de,
    ate
  })

  return response?.data
}

notificacoesInteligentes.getMessages = async (api, secret, { offset = 0, limit = 30, phoneLike = undefined, messageLike = undefined, status = undefined, dateBegin = undefined, dateEnd = undefined } = {}) => {
  const response = await axios.get(`${API_HS_NI_ENDPOINT}/messages/microservices/messages`, {
    params: {
      api,
      secret,
      offset,
      limit,
      phoneLike,
      messageLike,
      status,
      dateBegin,
      dateEnd
    }
  })

  return response?.data || []
}

notificacoesInteligentes.getMessagesStatistics = async (api, secret, { offset = 0, limit = 30000, status = undefined, dateBegin = undefined, dateEnd = undefined } = {}) => {
  const response = await axios.get(`${API_HS_NI_ENDPOINT}/messages/microservices/messageStatistics`, {
    params: {
      api,
      secret,
      offset,
      limit,
      status,
      dateBegin,
      dateEnd
    }
  })

  return response?.data?.statistics
}

// Série diária de envios (sucesso/erro/fila por dia, BRT) para o gráfico de
// desempenho + o veredito de saúde da conta Meta (accountHealth), que o backend
// calcula a partir das falhas recentes de pagamento. dateBegin/dateEnd seguem a
// convenção do relatório (dateEnd exclusivo).
notificacoesInteligentes.getReport = async (api, secret, { dateBegin = undefined, dateEnd = undefined } = {}) => {
  const response = await axios.get(`${API_HS_NI_ENDPOINT}/messages/microservices/messageReport`, {
    params: {
      api,
      secret,
      dateBegin,
      dateEnd
    }
  })

  return {
    series: response?.data?.series || [],
    accountHealth: response?.data?.accountHealth || null
  }
}

// Pulso da instância pro topo ao vivo do relatório: estado do farol, fila,
// ritmo de saída, horários das últimas saídas e os últimos acontecimentos. O
// backend segura 3s de cache por instância, então consultar a cada poucos
// segundos com a tela aberta é o uso esperado.
notificacoesInteligentes.getPulso = async (api, secret) => {
  const response = await axios.get(`${API_HS_NI_ENDPOINT}/messages/microservices/pulso`, {
    params: { api, secret }
  })

  return response?.data?.pulso || null
}

// Análises do período (cards e PDFs). Todas seguem a convenção do relatório:
// dateBegin inclusivo, dateEnd exclusivo.
notificacoesInteligentes.getRelatorioModelos = async (api, secret, { dateBegin, dateEnd } = {}) => {
  const response = await axios.get(`${API_HS_NI_ENDPOINT}/messages/microservices/relatorios/modelos`, {
    params: { api, secret, dateBegin, dateEnd }
  })

  return response?.data || null
}

notificacoesInteligentes.getRelatorioErros = async (api, secret, { dateBegin, dateEnd } = {}) => {
  const response = await axios.get(`${API_HS_NI_ENDPOINT}/messages/microservices/relatorios/erros`, {
    params: { api, secret, dateBegin, dateEnd }
  })

  return response?.data || null
}

// Lista das mensagens com falha dos motivos escolhidos — a parte detalhada do
// PDF. `chaves` são os códigos/textos que vieram de getRelatorioErros.
notificacoesInteligentes.getMensagensComFalha = async (api, secret, { dateBegin, dateEnd, chaves = [] } = {}) => {
  const response = await axios.get(`${API_HS_NI_ENDPOINT}/messages/microservices/relatorios/erros/mensagens`, {
    params: { api, secret, dateBegin, dateEnd, chaves: chaves.join(',') }
  })

  return response?.data || null
}

notificacoesInteligentes.getRelatorioInexistentes = async (api, secret, { dateBegin, dateEnd } = {}) => {
  const response = await axios.get(`${API_HS_NI_ENDPOINT}/messages/microservices/relatorios/inexistentes`, {
    params: { api, secret, dateBegin, dateEnd }
  })

  return response?.data || null
}

notificacoesInteligentes.sendMessage = async (api, secret, { message, phone, fileUrl } = {}) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/messages/sendMessage`, {
    clientApi: api,
    clientSecret: secret,
    message: encodeMessage(message, fileUrl),
    phone
  })

  return response?.data
}

notificacoesInteligentes.sendMessageGroup = async (api, secret, { message, phone, fileUrl } = {}) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/messages/sendMessageGroup`, {
    clientApi: api,
    clientSecret: secret,
    message: encodeMessage(message, fileUrl),
    phone
  })

  return response?.data
}

notificacoesInteligentes.resendMessage = async (messageId, api, secret) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/messages/microservices/resendMessage/${messageId}`, {
    clientApi: api,
    clientSecret: secret
  })

  return response?.data
}

notificacoesInteligentes.deleteMessage = async (messageId, api, secret) => {
  // No axios.delete o corpo vai em `data` (o segundo argumento é a config).
  // Antes a credencial ia solta na config e nada chegava ao master — a exclusão
  // só funcionava porque a rota não exigia credencial; agora exige e confere
  // que a mensagem é desta instância.
  const response = await axios.delete(`${API_HS_NI_ENDPOINT}/messages/microservices/delete/${messageId}`, {
    data: {
      clientApi: api,
      clientSecret: secret
    }
  })

  return response?.data
}

notificacoesInteligentes.changeOfficeHour = async (api, secret, { isEnabled, outOfHoursAction = 'send', days = [] } = {}) => {
  if(![OUT_OF_HOURS_ACTIONS.abort, OUT_OF_HOURS_ACTIONS.send].includes(outOfHoursAction)) {
    throw new Error('outOfHoursAction field invalid')
  }

  const response = await axios.put(`${API_HS_NI_ENDPOINT}/instances/microservices/changeOfficeHourService`, {
    clientApi: api,
    clientSecret: secret,
    officeHourService: {
      active: isEnabled,
      outOfHoursAction,
      days
    }
  })

  return response?.data
}

notificacoesInteligentes.expireQrCode = async (api, secret) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/expireQRCode`, {
    clientApi: api,
    clientSecret: secret
  })

  return response?.data
}

notificacoesInteligentes.restartApi = async (api, secret) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/reboot`, {
    clientApi: api,
    clientSecret: secret
  })

  return response?.data
}

notificacoesInteligentes.activateApi = async (api, secret) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/activate`, {
    clientApi: api,
    clientSecret: secret
  })

  return response?.data
}

notificacoesInteligentes.clearQueue = async (api, secret) => {
  const response = await axios.delete(`${API_HS_NI_ENDPOINT}/instances/microservices/clearQueue`, {
    data: {
      clientApi: api,
      clientSecret: secret
    }
  })

  return response?.data
}

notificacoesInteligentes.clearReport = async (api, secret) => {
  const response = await axios.delete(`${API_HS_NI_ENDPOINT}/messages/microservices/clearHistoric`, {
    data: {
      clientApi: api,
      clientSecret: secret
    }
  })

  return response?.data
}

// lastHours: janela do reenvio, em horas (o master aceita de 1 a 120 e usa 24
// quando o valor nao vem). Ate aqui a tela nunca mandava esse parametro, entao
// nao havia como alcancar uma falha de mais de um dia — o cliente via as
// mensagens antigas no relatorio e nenhum botao chegava nelas.
notificacoesInteligentes.resendMessagesWithErrors = async (api, secret, lastHours) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/messages/microservices/resendMessageError`, {
    clientApi: api,
    clientSecret: secret,
    ...(lastHours ? { lastHours } : {})
  })

  return response?.data
}

notificacoesInteligentes.deleteMessagesWithErrors = async (api, secret, periodToDelete) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/messages/microservices/clearErrors`, {
    clientApi: api,
    clientSecret: secret,
    periodToDelete
  })

  return response?.data
}

notificacoesInteligentes.changeDisconnectionNotificationNumber = async (api, secret, phone) => {
  const response = await axios.put(`${API_HS_NI_ENDPOINT}/instances/microservices/changeDisconnectionNotificationNumber`, {
    clientApi: api,
    clientSecret: secret,
    disconnectionNotificationNumber: phone
  })

  return response?.data
}

notificacoesInteligentes.changeDailyReportNotificationNumber = async (api, secret, phone) => {
  const response = await axios.put(`${API_HS_NI_ENDPOINT}/instances/microservices/changeDailyReportNotificationNumber`, {
    clientApi: api,
    clientSecret: secret,
    dailyReportNumber: phone
  })

  return response?.data
}

notificacoesInteligentes.updateDefaultMessage = async (api, secret, defaultMessage) => {
  const response = await axios.put(`${API_HS_NI_ENDPOINT}/instances/microservices/updateDefaultMessage`, {
    clientApi: api,
    clientSecret: secret,
    defaultMessage
  })

  return response?.data
}

notificacoesInteligentes.changeReadReceipts = async (api, secret, readReceiptsEnabled) => {
  const response = await axios.put(`${API_HS_NI_ENDPOINT}/instances/microservices/changeReadReceipts`, {
    clientApi: api,
    clientSecret: secret,
    readReceiptsEnabled
  })

  return response?.data
}

notificacoesInteligentes.changeMessageTimer = async (api, secret, { timerOverride = undefined, timerNewLine = undefined } = {}) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/setTimerOverride`, {
    clientApi: api,
    clientSecret: secret,
    timerOverride,
    timerNewLine
  })

  return response?.data
}

// Public endpoints
notificacoesInteligentes.getNotifications = async () => {
  const response = await axios.get(`${API_HS_NI_ENDPOINT}/notifications`)
  return response?.data
}

// Helpers
function encodeMessage(message, fileUrl) {
  const filename = getFilenameByUrl(fileUrl)
  const filenameTag = filename === '' ? '' : `%Filename%${filename}%/Filename%`

  return fileUrl === ''
    ? message
    : `${message}%SendFile%${filenameTag}%Url%${fileUrl.split(/\s/).join('')}%/Url%%/SendFile%`
}

function getFilenameByUrl(url) {
  return url ? url.substring(url.lastIndexOf('/') + 1) : ''
}

// ---------------------------------------------------------------------------
// HiperPay — Payment Page Profiles
// ---------------------------------------------------------------------------

export const HIPERPAY_ENDPOINT = process.env.REACT_APP_HIPERPAY_ENDPOINT || 'http://localhost:3003'

const hiperpay = {}

function hiperpayAuthHeader(clientId, clientSecret) {
  return { Authorization: `Basic ${btoa(`${clientId}:${clientSecret}`)}` }
}

// Catálogo de fontes da logo em texto. Vem do HiperPay para o painel não manter
// uma cópia da lista, que divergiria na primeira fonte nova.
// Modelos prontos de banner. Servidos pelo HiperPay para o painel não manter
// cópia da lista — modelo novo aparece aqui sozinho.
hiperpay.getBannerTemplates = async (clientId, clientSecret) => {
  const response = await axios.get(`${HIPERPAY_ENDPOINT}/api/banner-templates`, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.getLogoFonts = async (clientId, clientSecret) => {
  const response = await axios.get(`${HIPERPAY_ENDPOINT}/api/logo-fonts`, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.getProfiles = async (clientId, clientSecret) => {
  const response = await axios.get(`${HIPERPAY_ENDPOINT}/api/profiles`, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.createProfile = async (clientId, clientSecret, data) => {
  const response = await axios.post(`${HIPERPAY_ENDPOINT}/api/profiles`, data, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.updateProfile = async (clientId, clientSecret, id, data) => {
  const response = await axios.put(`${HIPERPAY_ENDPOINT}/api/profiles/${id}`, data, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.deleteProfile = async (clientId, clientSecret, id) => {
  const response = await axios.delete(`${HIPERPAY_ENDPOINT}/api/profiles/${id}`, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.setDefaultProfile = async (clientId, clientSecret, id) => {
  const response = await axios.patch(`${HIPERPAY_ENDPOINT}/api/profiles/${id}/default`, {}, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.getSettings = async (clientId, clientSecret) => {
  const response = await axios.get(`${HIPERPAY_ENDPOINT}/api/integration/settings`, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.updateSettings = async (clientId, clientSecret, data) => {
  const response = await axios.patch(`${HIPERPAY_ENDPOINT}/api/integration/settings`, data, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.getBanners = async (clientId, clientSecret) => {
  const response = await axios.get(`${HIPERPAY_ENDPOINT}/api/banners`, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.createBanner = async (clientId, clientSecret, data) => {
  const response = await axios.post(`${HIPERPAY_ENDPOINT}/api/banners`, data, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.updateBanner = async (clientId, clientSecret, id, data) => {
  const response = await axios.put(`${HIPERPAY_ENDPOINT}/api/banners/${id}`, data, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

hiperpay.deleteBanner = async (clientId, clientSecret, id) => {
  const response = await axios.delete(`${HIPERPAY_ENDPOINT}/api/banners/${id}`, {
    headers: hiperpayAuthHeader(clientId, clientSecret)
  })
  return response?.data
}

notificacoesInteligentes.setupHiperPay = async (api, secret) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/setupHiperPay`, {
    clientApi: api,
    clientSecret: secret
  })
  return response?.data
}

notificacoesInteligentes.disconnectHiperPay = async (api, secret) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/instances/microservices/disconnectHiperPay`, {
    clientApi: api,
    clientSecret: secret
  })
  return response?.data
}

// ---------------------------------------------------------------------------
// SmartFilter — filtro inteligente de mensagens (proxy via master -> secondary)
// Autentica por header api/secret (o master resolve a instancia e encaminha).
// ---------------------------------------------------------------------------

const smartFilter = {}

function sfConfig(api, secret, extra = {}) {
  // timeout em todas as chamadas do SmartFilter: sem isso, se o backend pendurar
  // (ex.: pool de conexões do secondary esgotado), o axios espera pra sempre e o
  // painel fica "carregando infinito". Com 20s, no pior caso mostra "Falha ao
  // carregar" em vez de spinner eterno. (O proxy do master já corta em 15s.)
  return { headers: { api, secret }, timeout: 20000, ...extra }
}

const SF = `${API_HS_NI_ENDPOINT}/smart-filter`

smartFilter.getSettings = async (api, secret) => {
  const response = await axios.get(`${SF}/settings`, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.updateSettings = async (api, secret, data) => {
  const response = await axios.put(`${SF}/settings`, data, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.listTemplates = async (api, secret) => {
  const response = await axios.get(`${SF}/templates`, sfConfig(api, secret))
  return response?.data?.data || []
}

smartFilter.importTemplate = async (api, secret, code) => {
  const response = await axios.post(`${SF}/templates/${code}/import`, {}, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.listRules = async (api, secret) => {
  const response = await axios.get(`${SF}/rules`, sfConfig(api, secret))
  return response?.data?.data || []
}

smartFilter.createRule = async (api, secret, data) => {
  const response = await axios.post(`${SF}/rules`, data, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.updateRule = async (api, secret, id, data) => {
  const response = await axios.put(`${SF}/rules/${id}`, data, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.toggleRule = async (api, secret, id) => {
  const response = await axios.put(`${SF}/rules/${id}/toggle`, {}, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.deleteRule = async (api, secret, id) => {
  const response = await axios.delete(`${SF}/rules/${id}`, sfConfig(api, secret))
  return response?.data
}

smartFilter.getStats = async (api, secret, { days = 7, dateBegin = undefined, dateEnd = undefined } = {}) => {
  const response = await axios.get(`${SF}/stats`, sfConfig(api, secret, { params: { days, dateBegin, dateEnd } }))
  return response?.data?.data
}

smartFilter.listDecisions = async (api, secret, { limit = 50, skip = 0, decision, source, phone } = {}) => {
  const response = await axios.get(`${SF}/decisions`, sfConfig(api, secret, {
    params: { limit, skip, decision, source, phone }
  }))
  return response?.data
}

smartFilter.getDecisionDetail = async (api, secret, id) => {
  const response = await axios.get(`${SF}/decisions/${id}`, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.getBudget = async (api, secret) => {
  const response = await axios.get(`${SF}/budget`, sfConfig(api, secret))
  return response?.data
}

smartFilter.getDelinquency = async (api, secret, { months, page = 1, limit = 20 } = {}) => {
  const response = await axios.get(`${SF}/delinquency`, sfConfig(api, secret, { params: { months, page, limit } }))
  return response?.data
}

smartFilter.listSuppression = async (api, secret) => {
  const response = await axios.get(`${SF}/suppression`, sfConfig(api, secret))
  return response?.data?.data || []
}

smartFilter.addSuppression = async (api, secret, data) => {
  const response = await axios.post(`${SF}/suppression`, data, sfConfig(api, secret))
  // Devolve o envelope inteiro: além do registro (`data`), o backend diz se o
  // número já era um opt-out real e foi mantido (`optOutPreservado`).
  return response?.data
}

smartFilter.bulkAddSuppression = async (api, secret, items, { reason = 'manual', notes } = {}) => {
  const response = await axios.post(`${SF}/suppression/bulk`, { items, reason, notes }, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.bulkRemoveSuppression = async (api, secret, phones) => {
  const response = await axios.post(`${SF}/suppression/bulk-delete`, { phones }, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.removeSuppression = async (api, secret, phone) => {
  const response = await axios.delete(`${SF}/suppression/${encodeURIComponent(phone)}`, sfConfig(api, secret))
  return response?.data
}

smartFilter.listAllowList = async (api, secret) => {
  const response = await axios.get(`${SF}/allow-list`, sfConfig(api, secret))
  return response?.data?.data || []
}

smartFilter.addAllowList = async (api, secret, data) => {
  const response = await axios.post(`${SF}/allow-list`, data, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.bulkAddAllowList = async (api, secret, items, { notes } = {}) => {
  const response = await axios.post(`${SF}/allow-list/bulk`, { items, notes }, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.bulkRemoveAllowList = async (api, secret, phones) => {
  const response = await axios.post(`${SF}/allow-list/bulk-delete`, { phones }, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.removeAllowList = async (api, secret, phone) => {
  const response = await axios.delete(`${SF}/allow-list/${encodeURIComponent(phone)}`, sfConfig(api, secret))
  return response?.data
}

smartFilter.resetData = async (api, secret, { deleteBefore } = {}) => {
  const body = deleteBefore ? { deleteBefore } : {}
  const response = await axios.post(`${SF}/reset`, body, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.listApprovals = async (api, secret, { status = 'pending' } = {}) => {
  const response = await axios.get(`${SF}/approvals`, sfConfig(api, secret, { params: { status } }))
  return response?.data?.data || []
}

smartFilter.approveApproval = async (api, secret, id) => {
  const response = await axios.put(`${SF}/approvals/${id}/approve`, {}, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.rejectApproval = async (api, secret, id) => {
  const response = await axios.put(`${SF}/approvals/${id}/reject`, {}, sfConfig(api, secret))
  return response?.data?.data
}

smartFilter.getRuleRanking = async (api, secret, { days = 7, dateBegin = undefined, dateEnd = undefined } = {}) => {
  const response = await axios.get(`${SF}/stats/rule-ranking`, sfConfig(api, secret, { params: { days, dateBegin, dateEnd } }))
  return response?.data?.data || []
}

smartFilter.getHourHeatmap = async (api, secret, { days = 7, dateBegin = undefined, dateEnd = undefined } = {}) => {
  const response = await axios.get(`${SF}/stats/hour-heatmap`, sfConfig(api, secret, { params: { days, dateBegin, dateEnd } }))
  return response?.data?.data || { hours: Array(24).fill(0) }
}

// ---------------------------------------------------------------------------
// Templates Meta por conexão (master -> Graph API). Todas as rotas são POST
// com clientApi/clientSecret no corpo — mesmo padrão do snapshot da WABA.
// O token da Meta nunca trafega por aqui: quem fala com a Graph é o master.
// ---------------------------------------------------------------------------

const metaTemplates = {}

const MT = `${API_HS_NI_ENDPOINT}/instances/microservices/metaTemplates`

async function mtPost(path, api, secret, params = {}) {
  const response = await axios.post(`${MT}/${path}`, {
    clientApi: api,
    clientSecret: secret,
    ...params
  })
  return response?.data
}

// Registros persistidos desta conexão + identificação da WABA
metaTemplates.list = async (api, secret) => mtPost('list', api, secret)

// Biblioteca de modelos recomendados (com flag installed por nome+idioma).
// params: { idioma: 'todos' | 'pt_BR' | 'es' } — sem idioma o master devolve
// só pt_BR (compatibilidade com painel antigo); o diálogo pede 'todos' e filtra.
metaTemplates.library = async (api, secret, params = {}) => mtPost('library', api, secret, params)

// Valida e normaliza uma definição sem efeito colateral
// params: { globalTemplateId } OU { template: { name, language, category, components } }
metaTemplates.preview = async (api, secret, params = {}) => mtPost('preview', api, secret, params)

// Cria o registro local e submete o create na Graph API
// params: { globalTemplateId } OU { template: {...} }
metaTemplates.install = async (api, secret, params = {}) => mtPost('install', api, secret, params)

// Instala todos os da biblioteca ainda não instalados nesta conexão
metaTemplates.installRecommended = async (api, secret) => mtPost('installRecommended', api, secret)

// Reconcilia TODOS os registros desta conexão com a WABA (pagina na Graph)
metaTemplates.sync = async (api, secret) => mtPost('sync', api, secret)

// Reconcilia 1 registro (templateId = _id do registro local)
metaTemplates.syncOne = async (api, secret, templateId) => mtPost('syncOne', api, secret, { templateId })

// Re-submete um registro com falha local ou rejeitado pela Meta
metaTemplates.retry = async (api, secret, templateId) => mtPost('retry', api, secret, { templateId })

// Edita um template que JÁ EXISTE na Meta (corrigir e reenviar sem excluir):
// só APPROVED/REJECTED/PAUSED; nome e idioma não mudam. Volta pra análise.
// params: { templateId, template: { name, language, category, components } }
metaTemplates.edit = async (api, secret, params = {}) => mtPost('edit', api, secret, params)

// Exclui o template da WABA (Graph) e remove o registro local desta conexão
// (templateId = _id do registro local). Idempotente do lado do master.
metaTemplates.remove = async (api, secret, templateId) => mtPost('delete', api, secret, { templateId })

// ---------------------------------------------------------------------------
// Mensagens agendadas — envios que saem sozinhos, no dia e hora definidos.
// O master autentica estas rotas por HEADER (api/secret), igual ao SmartFilter,
// e não por credencial no corpo. O timeout existe pelo mesmo motivo de lá: sem
// ele, backend pendurado deixa a tela em "carregando" pra sempre.
// ---------------------------------------------------------------------------
const scheduledMessages = {}

const SM = `${API_HS_NI_ENDPOINT}/scheduled-messages`

function smConfig(api, secret, extra = {}) {
  return { headers: { api, secret }, timeout: 20000, ...extra }
}

scheduledMessages.list = async (api, secret) => {
  const response = await axios.get(SM, smConfig(api, secret))
  return Array.isArray(response?.data) ? response.data : []
}

scheduledMessages.create = async (api, secret, payload = {}) => {
  const response = await axios.post(SM, payload, smConfig(api, secret))
  return response?.data
}

scheduledMessages.update = async (api, secret, id, payload = {}) => {
  const response = await axios.put(`${SM}/${id}`, payload, smConfig(api, secret))
  return response?.data
}

// Pausar e retomar são o mesmo update — o master aceita status active|paused e,
// ao pausar, cancela as execuções que ainda não saíram.
scheduledMessages.setStatus = async (api, secret, id, status) => {
  const response = await axios.put(`${SM}/${id}`, { status }, smConfig(api, secret))
  return response?.data
}

scheduledMessages.remove = async (api, secret, id) => {
  const response = await axios.delete(`${SM}/${id}`, smConfig(api, secret))
  return response?.data
}

scheduledMessages.history = async (api, secret, id) => {
  const response = await axios.get(`${SM}/${id}/history`, smConfig(api, secret))
  return Array.isArray(response?.data) ? response.data : []
}

// Disparo em massa (tela "Enviar mensagem"). Quem confere custo e saldo e
// decide se o disparo pode existir é o master; a tela só mostra. POST com a
// credencial da conexão no corpo; leituras com api/secret na query, como o
// pulso do relatório.
const disparos = {}
const URL_DISPAROS = `${API_HS_NI_ENDPOINT}/messages/microservices/disparos`
// Teto de tempo: sem ele, um master pendurado deixava a tela presa em
// "Disparando…". A criação tem mais folga (refaz a conferência sem cache).
const TETO_LEITURA_MS = 20000
const TETO_CRIACAO_MS = 45000

disparos.conferir = async (api, secret, { destinatarios, conteudo, limitarA } = {}) => {
  const response = await axios.post(`${URL_DISPAROS}/conferencia`, {
    clientApi: api,
    clientSecret: secret,
    destinatarios,
    conteudo,
    ...(limitarA ? { limitarA } : {})
  }, { timeout: TETO_LEITURA_MS })
  return response?.data
}

disparos.criar = async (api, secret, { destinatarios, conteudo, chaveIdempotencia, limitarA } = {}) => {
  const response = await axios.post(URL_DISPAROS, {
    clientApi: api,
    clientSecret: secret,
    destinatarios,
    conteudo,
    chaveIdempotencia,
    ...(limitarA ? { limitarA } : {})
  }, { timeout: TETO_CRIACAO_MS })
  return response?.data
}

disparos.listar = async (api, secret) => {
  const response = await axios.get(URL_DISPAROS, { params: { api, secret }, timeout: TETO_LEITURA_MS })
  return Array.isArray(response?.data) ? response.data : []
}

disparos.detalhe = async (api, secret, id) => {
  const response = await axios.get(`${URL_DISPAROS}/${id}`, { params: { api, secret }, timeout: TETO_LEITURA_MS })
  return response?.data
}

disparos.cancelar = async (api, secret, id) => {
  const response = await axios.post(`${URL_DISPAROS}/${id}/cancelar`, { clientApi: api, clientSecret: secret }, { timeout: TETO_LEITURA_MS })
  return response?.data
}

// "Reenviar com erro": quantas voltam pra fila e quanto podem custar, antes do
// PROSSEGUIR.
notificacoesInteligentes.conferirReenvio = async (api, secret, lastHours) => {
  const response = await axios.post(`${API_HS_NI_ENDPOINT}/messages/microservices/resendMessageError/conferencia`, {
    clientApi: api,
    clientSecret: secret,
    lastHours
  })
  return response?.data
}

// Contrato do Piloto: leituras com api/secret; gravações com clientApi/clientSecret.
const URL_HIPERSYNC = `${API_HS_NI_ENDPOINT}/hipersync/microservices`
const TETO_ENSAIO_MS = 120000
const lerHipersync = async (caminho, api, secret, filtros = {}, timeout = TETO_LEITURA_MS) => {
  const resposta = await axios.get(`${URL_HIPERSYNC}${caminho}`, { params: { ...filtros, api, secret }, timeout })
  return resposta?.data
}
const gravarHipersync = async (metodo, caminho, api, secret, dados) => {
  const resposta = await axios[metodo](`${URL_HIPERSYNC}${caminho}`, { ...dados, clientApi: api, clientSecret: secret }, { timeout: TETO_CRIACAO_MS })
  return resposta?.data
}
const hipersync = {
  hoje: (api, secret) => lerHipersync('/integracao/hoje', api, secret, {}, TETO_ENSAIO_MS),
  enviarHoje: (api, secret, dados) => gravarHipersync('post', '/integracao/hoje/enviar', api, secret, dados),
  pararHoje: (api, secret) => gravarHipersync('post', '/integracao/hoje/parar', api, secret, {}),
  conectores: (api, secret) => lerHipersync('/conectores', api, secret),
  // A integração traz a previsão (controle), que consulta o sistema: mesma folga das leituras pesadas.
  integracao: (api, secret) => lerHipersync('/integracao', api, secret, {}, TETO_ENSAIO_MS),
  testar: (api, secret, dados) => gravarHipersync('post', '/integracao/testar', api, secret, dados),
  salvar: (api, secret, dados) => gravarHipersync('put', '/integracao', api, secret, dados),
  salvarRotina: (api, secret, chave, dados) => gravarHipersync('put', `/integracao/rotinas/${encodeURIComponent(chave)}`, api, secret, dados),
  ensaio: (api, secret, data, maxDiasAtraso) => lerHipersync('/integracao/ensaio', api, secret, { data, ...(maxDiasAtraso !== undefined ? { maxDiasAtraso } : {}) }, TETO_ENSAIO_MS),
  manual: (api, secret, rotina, data) => lerHipersync('/integracao/manual', api, secret, { rotina, data }, TETO_ENSAIO_MS),
  enviarManual: (api, secret, dados) => gravarHipersync('post', '/integracao/manual', api, secret, dados),
  acompanhamento: (api, secret, filtros) => lerHipersync('/integracao/acompanhamento', api, secret, filtros),
  enviarTeste: (api, secret, dados) => gravarHipersync('post', '/integracao/teste', api, secret, dados),
  pausar: (api, secret) => gravarHipersync('post', '/integracao/pausar', api, secret, {}),
  retomar: (api, secret, dados = {}) => gravarHipersync('post', '/integracao/retomar', api, secret, dados)
}

// "Já uso o HiperWaba": o cliente gera aqui o código que cola no GLA Conecta.
// POST com a credencial da conexão no corpo, como as outras rotas microservices.
// O código só aparece na resposta do `gerarCodigo` — o `estado` nunca o devolve
// (o master guarda só o hash), por isso a tela não tem como mostrá-lo de novo.
const URL_CONECTA = `${API_HS_NI_ENDPOINT}/instances/microservices/conecta`
const conecta = {
  estado: async (api, secret) => {
    const response = await axios.post(`${URL_CONECTA}/estado`, { clientApi: api, clientSecret: secret }, { timeout: TETO_LEITURA_MS })
    return response?.data
  },
  gerarCodigo: async (api, secret) => {
    const response = await axios.post(`${URL_CONECTA}/codigo`, { clientApi: api, clientSecret: secret }, { timeout: TETO_LEITURA_MS })
    return response?.data
  }
}

export default {
  conecta,
  notificacoesInteligentes,
  hiperpay,
  smartFilter,
  metaTemplates,
  scheduledMessages,
  disparos,
  hipersync
}

